import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Workbook } from 'exceljs';
import * as mammoth from 'mammoth';
import { AttachmentPreviewKind, NotificationAttachment } from 'src/app/shared/models/notification-attachment.model';
import { NotificationAttachmentService } from 'src/app/shared/services/notification-attachment.service';
import { getFileIconClass, getFileTypeLabel, getPreviewKind } from 'src/app/shared/utils/file-type.util';

type PreviewState = 'loading' | 'ready' | 'error' | 'unsupported';

@Component({
  selector: 'app-attachment-preview-dialog',
  templateUrl: './attachment-preview-dialog.component.html',
  styleUrls: ['./attachment-preview-dialog.component.scss']
})
export class AttachmentPreviewDialogComponent implements OnInit, OnDestroy {
  @Input() attachment!: NotificationAttachment;

  state: PreviewState = 'loading';
  errorMessage = '';
  blobUrl = '';
  safeBlobUrl: SafeResourceUrl | null = null;
  wordHtml: SafeHtml | null = null;
  excelHtml: SafeHtml | null = null;
  previewKind: AttachmentPreviewKind = 'unsupported';
  fileIconClass = 'icon-file';
  fileTypeLabel = 'File';
  imageZoom = 1;

  constructor(
    public activeModal: NgbActiveModal,
    private sanitizer: DomSanitizer,
    private attachmentService: NotificationAttachmentService
  ) {}

  ngOnInit(): void {
    if (!this.attachment) {
      this.state = 'error';
      this.errorMessage = 'No attachment provided.';
      return;
    }
    this.previewKind = getPreviewKind(this.attachment.fileName, this.attachment.mimeType);
    this.fileIconClass = getFileIconClass(this.attachment.fileName, this.attachment.mimeType);
    this.fileTypeLabel = getFileTypeLabel(this.attachment.fileName, this.attachment.mimeType);
    this.loadPreview();
  }

  ngOnDestroy(): void {
    this.cleanupBlobUrl();
    this.attachmentService.revokeAllObjectUrls();
  }

  download(): void {
    this.attachmentService.downloadFile(this.attachment);
  }

  close(): void {
    this.activeModal.dismiss();
  }

  zoomIn(): void {
    this.imageZoom = Math.min(this.imageZoom + 0.25, 3);
  }

  zoomOut(): void {
    this.imageZoom = Math.max(this.imageZoom - 0.25, 0.5);
  }

  resetZoom(): void {
    this.imageZoom = 1;
  }

  private loadPreview(): void {
    this.state = 'loading';
    this.attachmentService.fetchBlob(this.attachment).subscribe({
      next: async (blob) => {
        try {
          await this.renderPreview(blob);
        } catch (err) {
          this.state = 'error';
          this.errorMessage = 'Unable to render preview.';
        }
      },
      error: () => {
        this.state = 'error';
        this.errorMessage = 'Failed to load the attachment.';
      }
    });
  }

  private async renderPreview(blob: Blob): Promise<void> {
    if (this.previewKind === 'unsupported') {
      this.state = 'unsupported';
      return;
    }

    if (this.previewKind === 'image' || this.previewKind === 'pdf') {
      this.cleanupBlobUrl();
      this.blobUrl = this.attachmentService.createObjectUrl(blob);
      this.safeBlobUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
      this.state = 'ready';
      return;
    }

    if (this.previewKind === 'word') {
      const arrayBuffer = await blob.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      this.wordHtml = this.sanitizer.bypassSecurityTrustHtml(result.value || '<p>No content</p>');
      this.state = 'ready';
      return;
    }

    if (this.previewKind === 'excel') {
      const arrayBuffer = await blob.arrayBuffer();
      const workbook = new Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const sheet = workbook.worksheets[0];
      if (!sheet) {
        this.state = 'unsupported';
        return;
      }
      const maxRows = 50;
      const maxCols = 20;
      let html = '<div class="excel-preview-wrap"><table class="excel-preview-table"><tbody>';
      sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber > maxRows) { return; }
        html += '<tr>';
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          if (colNumber > maxCols) { return; }
          const value = cell.value == null ? '' : String(cell.value);
          html += `<td>${this.escapeHtml(value)}</td>`;
        });
        html += '</tr>';
      });
      html += '</tbody></table></div>';
      this.excelHtml = this.sanitizer.bypassSecurityTrustHtml(html);
      this.state = 'ready';
      return;
    }

    this.state = 'unsupported';
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private cleanupBlobUrl(): void {
    if (this.blobUrl) {
      this.attachmentService.revokeObjectUrl(this.blobUrl);
      this.blobUrl = '';
      this.safeBlobUrl = null;
    }
  }
}
